/**
 * Phase 1.7 — Plantings module index, v2.
 *
 * Group-by: status (planned / sown / growing / harvesting / complete /
 * failed). Plantings rely on a planner-augmented response that joins
 * variety_name, crop_name, and pasture_name; we use those when the
 * server supplies them and gracefully degrade when not.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, Plus, Sprout, Trash2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, EmptyState, FilterBar, KpiCard, SectionHeader, ViewToggle, type ViewMode } from '@/components/v2';
import { authFetch } from '@/lib/auth';
import { useUiPrefs } from '@/lib/layout-flag';
import { usePageHeader } from '@/components/v2/layout/Header';

interface Planting {
  id: string;
  ranch_id: string;
  crop_variety_id: string;
  growing_template_id: string;
  pasture_id: string;
  field_subdivision_id: string | null;
  sow_date: string;
  transplant_date: string | null;
  expected_harvest_date: string | null;
  actual_harvest_date: string | null;
  status: 'planned' | 'sown' | 'growing' | 'harvesting' | 'complete' | 'failed';
  quantity_planted: number | null;
  quantity_unit: string | null;
  notes: string | null;
  variety_name?: string;
  crop_name?: string;
  pasture_name?: string;
}

type Status = Planting['status'];
const STATUS_ORDER: Status[] = ['planned', 'sown', 'growing', 'harvesting', 'complete', 'failed'];
const STATUS_LABEL: Record<Status, string> = {
  planned: 'Planned',
  sown: 'Sown',
  growing: 'Growing',
  harvesting: 'Harvesting',
  complete: 'Complete',
  failed: 'Failed',
};
const STATUS_TONE: Record<Status, string> = {
  planned: 'bg-sky-muted/10 text-sky-muted dark:bg-sky-muted-dark/20 dark:text-sky-muted-dark',
  sown: 'bg-olive/15 text-olive dark:bg-olive-dark/20 dark:text-olive-dark',
  growing: 'bg-moss/10 text-moss dark:bg-moss-dark/20 dark:text-moss-dark',
  harvesting: 'bg-burnt-orange/10 text-burnt-orange dark:bg-burnt-orange-dark/20 dark:text-burnt-orange-dark',
  complete: 'bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark',
  failed: 'bg-clay/10 text-clay dark:bg-clay-dark/20 dark:text-clay-dark',
};

const VIEW_KEY = 'plantings';

export function PlantingsPageV2(): JSX.Element {
  const navigate = useNavigate();
  const { prefs, patch } = useUiPrefs();
  const view: ViewMode = prefs.view_modes?.[VIEW_KEY] ?? 'grid';
  const [items, setItems] = useState<Planting[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | Status>('all');

  // The "New planting" flow has FK dependencies (crop_variety, growing_template,
  // pasture) and is owned by /crops/planner. The header button navigates there
  // rather than re-implementing a thin add form that can't satisfy the schema.
  usePageHeader(
    'Plantings',
    <div className="flex items-center gap-2">
      <Button asChild variant="outline" size="sm">
        <Link to="/crops/planner">
          <Calendar className="h-4 w-4" />
          Planner
        </Link>
      </Button>
      <Button
        asChild
        size="sm"
        className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
      >
        <Link to="/crops/planner">
          <Plus className="h-4 w-4" />
          New planting
        </Link>
      </Button>
    </div>,
  );

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        const data = await authFetch<{ items: Planting[] }>('/api/plantings');
        if (!cancelled) setItems(data.items ?? []);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load plantings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo<Planting[]>(() => {
    const q = search.trim().toLowerCase();
    return items.filter((p) => {
      if (q) {
        const haystack = `${p.crop_name ?? ''} ${p.variety_name ?? ''} ${p.pasture_name ?? ''} ${p.notes ?? ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      return true;
    });
  }, [items, search, statusFilter]);

  const grouped = useMemo<Record<Status, Planting[]>>(() => {
    const out = STATUS_ORDER.reduce<Record<Status, Planting[]>>(
      (acc, s) => ({ ...acc, [s]: [] }),
      {} as Record<Status, Planting[]>,
    );
    for (const p of filtered) out[p.status].push(p);
    return out;
  }, [filtered]);

  const totals = useMemo(() => {
    const counts: Record<Status, number> = {
      planned: 0, sown: 0, growing: 0, harvesting: 0, complete: 0, failed: 0,
    };
    for (const p of items) counts[p.status] += 1;
    return counts;
  }, [items]);

  const setView = (next: ViewMode): void => {
    void patch({ view_modes: { ...(prefs.view_modes ?? {}), [VIEW_KEY]: next } });
  };

  const hasActiveFilters = Boolean(search) || statusFilter !== 'all';
  const clear = (): void => { setSearch(''); setStatusFilter('all'); };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Active"
          icon={Sprout}
          primary={{ label: 'Growing', value: totals.growing, tone: 'positive' }}
          secondary={{ label: 'Harvesting', value: totals.harvesting }}
        />
        <KpiCard label="Pipeline" primary={{ label: 'Planned', value: totals.planned }} secondary={{ label: 'Sown', value: totals.sown }} />
        <KpiCard label="Closed" primary={{ label: 'Complete', value: totals.complete, tone: 'positive' }} secondary={{ label: 'Failed', value: totals.failed, tone: 'warning' }} />
        <KpiCard label="Filter" primary={{ label: 'Showing', value: filtered.length }} secondary={{ label: 'Total', value: items.length }} />
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search crops, varieties, pastures' }}
        filters={[
          <FilterSelect
            key="status"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as 'all' | Status)}
            options={[
              { value: 'all', label: 'All statuses' },
              ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
            ]}
          />,
        ]}
        actions={<ViewToggle value={view} onChange={setView} />}
        onClear={clear}
        hasActiveFilters={hasActiveFilters}
      />

      {error ? <div className="rounded-lg bg-clay/10 p-4 text-sm text-clay dark:bg-clay-dark/20 dark:text-clay-dark">{error}</div> : null}

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Sprout}
          title={hasActiveFilters ? 'No matches' : 'No plantings yet'}
          description={hasActiveFilters ? 'Try clearing filters.' : 'Plan a planting to track sow dates, transplants, and expected harvests.'}
          action={
            hasActiveFilters ? (
              <Button onClick={clear} variant="outline">Clear filters</Button>
            ) : (
              <Button asChild>
                <Link to="/crops/planner">
                  <Plus className="h-4 w-4" />
                  Open planner
                </Link>
              </Button>
            )
          }
        />
      ) : (
        STATUS_ORDER.filter((s) => grouped[s].length > 0).map((status) => (
          <SectionHeader key={status} title={STATUS_LABEL[status]} count={grouped[status].length} collapsible>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {grouped[status].map((p) => (
                <Card
                  key={p.id}
                  interactive
                  onClick={() => navigate(`/plantings/${p.id}`)}
                  title={p.variety_name ?? p.crop_name ?? 'Planting'}
                  subtitle={p.pasture_name ? `In ${p.pasture_name}` : 'No location'}
                  badge={
                    <span className={`rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[p.status]}`}>
                      {STATUS_LABEL[p.status]}
                    </span>
                  }
                  footer={
                    <div className="flex items-center justify-between text-xs">
                      <span>
                        Sown {format(parseISO(p.sow_date), 'MMM d')}
                        {p.expected_harvest_date ? ` · harvest ${format(parseISO(p.expected_harvest_date), 'MMM d')}` : ''}
                      </span>
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                    </div>
                  }
                />
              ))}
            </div>
          </SectionHeader>
        ))
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-2 text-sm shadow-sm dark:bg-surface-dark dark:border-border"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
